import styled, { css, keyframes } from 'styled-components';

import { breakpoints } from 'theme';

const primary = '#00696b';
const primaryLight = '#00a4a6';
const surface = '#f7f9fb';
const onSurface = '#191c1e';
const onVariant = '#3d4949';
const cardShadow = '0 4px 24px rgba(0, 50, 51, 0.05)';

const planGradient = css`
  background: linear-gradient(135deg, ${primary}, ${primaryLight});
`;

const headlineFont = css`
  font-family: 'Open Sans', sans-serif;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

export const PageRoot = styled.div`
  min-height: 100%;
  background: ${surface};
  color: ${onSurface};
  text-align: left;
  position: relative;
  width: 100%;
  max-width: 100vw;
`;

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #f1f5f9;
`;

export const TopBarTitle = styled.h1`
  ${headlineFont}
  font-size: 18px;
  margin: 0;
  color: ${onSurface};
`;

export const Main = styled.main`
  padding: 24px;
  max-width: 1024px;
  margin: 0 auto 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${breakpoints.downSmPlus} {
    padding: 16px;
  }
`;

export const FloatingLoaderWrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  pointer-events: all;
`;

/* Hero - no trial */
export const HeroCard = styled.div`
  margin-top: 8px;
  background: #fff;
  border-radius: 16px;
  box-shadow: ${cardShadow};
  overflow: hidden;
  display: flex;
  align-items: stretch;
`;

export const HeroAccent = styled.div`
  width: 6px;
  flex-shrink: 0;
  ${planGradient}
`;

export const HeroInner = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

export const HeroLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

export const IconBox = styled.div<{ $filled?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  ${({ $filled }) =>
    $filled ? planGradient : 'background: #f8fafc; color: #94a3b8;'}
`;

export const HeroTitle = styled.h2`
  ${headlineFont}
  font-size: 16px;
  margin: 0 0 4px;
  color: ${onSurface};
`;

export const HeroSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${onVariant};
  line-height: 1.45;
`;

export const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const TextLink = styled.a`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const btnSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const BtnSpinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${btnSpin} 0.7s linear infinite;
  flex-shrink: 0;
`;

export const BtnPrimary = styled.button`
  ${planGradient}
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 24px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const BtnOutline = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  border-radius: 12px;
  border: 2px solid ${primaryLight};
  background: transparent;
  color: ${primary};
  font-family: inherit;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: rgba(0, 164, 166, 0.06);
  }

  &:disabled,
  &[aria-disabled='true'] {
    border-color: #cbd5e1;
    color: #94a3b8;
    background: #f8fafc;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export const BillingPortalPlanButton = styled(BtnOutline)`
  width: auto;
  flex: 1 1 180px;
  max-width: 100%;
`;

export const TextActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 8px 2px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #64748b;
    text-decoration: underline;
  }
`;

/* Trial banner */
export const TrialBanner = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #fff8ed;
  border-left: 4px solid #f5a623;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 50, 51, 0.04);
  width: 100%;
  box-sizing: border-box;

  @media ${breakpoints.downSmPlus} {
    padding: 16px;
    gap: 12px;
  }
`;

export const TrialBannerIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-top: 2px;
`;

export const TrialBannerContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TrialBannerTitle = styled.h3`
  ${headlineFont}
  font-size: 14px;
  margin: 0 0 4px;
  color: #a06a0a;
`;

export const TrialBannerText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(160, 106, 10, 0.85);
`;

/* Active / cancelled / billing-attention info banner */
export const StatusBanner = styled.div<{
  $variant: 'success' | 'muted' | 'attention';
}>`
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  ${({ $variant }) =>
    $variant === 'success'
      ? css`
          background: #f0faf6;
          border: 1px solid rgba(0, 184, 168, 0.3);
        `
      : $variant === 'attention'
      ? css`
          background: #fff5f5;
          border: 1px solid rgba(226, 75, 74, 0.3);
        `
      : css`
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        `}

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
    gap: 12px;
  }
`;

export const StatusBannerTitle = styled.span`
  ${headlineFont}
  font-size: 14px;
  color: ${primary};
  word-break: break-word;
`;

export const StatusBannerBody = styled.span`
  font-size: 14px;
  color: ${onVariant};
  opacity: 0.85;
  margin-left: 8px;
  word-break: break-word;
`;

export const StatusBannerMain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const statusCheckPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 164, 166, 0.28);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 0 9px rgba(0, 164, 166, 0);
  }
`;

const statusAttentionPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(226, 75, 74, 0.28);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 0 9px rgba(226, 75, 74, 0);
  }
`;

export const StatusCheckWrap = styled.div<{ $tone?: 'success' | 'attention' }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  will-change: transform, box-shadow;
  ${({ $tone }) =>
    $tone === 'attention'
      ? css`
          background: rgba(226, 75, 74, 0.1);
          animation: ${statusAttentionPulse} 2.2s ease-in-out infinite;
        `
      : css`
          background: rgba(0, 164, 166, 0.1);
          animation: ${statusCheckPulse} 2.2s ease-in-out infinite;
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StatusBannerTextCol = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FrequencyPill = styled.span`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${primary};
  background: rgba(0, 164, 166, 0.1);
  padding: 6px 12px;
  border-radius: 999px;
  flex-shrink: 0;
  align-self: center;

  @media ${breakpoints.downSmPlus} {
    align-self: flex-start;
  }
`;

export const StatusBannerCta = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 10px 14px;
  border-radius: 8px;
  white-space: nowrap;
  background: #e24b4a;
  color: #ffffff;

  &:hover {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid #e24b4a;
    outline-offset: 2px;
  }

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    align-self: stretch;
    white-space: normal;
    text-align: center;
    line-height: 1.35;
  }
`;

export const UsageHintSmall = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  line-height: 1.35;
`;

export const UsageHintWarn = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #a06a0a;
  font-weight: 600;
  line-height: 1.35;
`;

export const UsageUpgradeNudge = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff8ed;
  border: 1px solid rgba(245, 166, 35, 0.4);
  border-radius: 12px;
`;

export const UsageUpgradeNudgeText = styled.div`
  flex: 1;
  font-size: 13px;
  line-height: 1.45;
  color: #a06a0a;

  strong {
    font-weight: 700;
    display: block;
    margin-bottom: 4px;
  }
`;

export const UsageUpgradeBtn = styled.a`
  flex-shrink: 0;
  ${planGradient}
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 12px;
  text-decoration: none;
  white-space: nowrap;
  align-self: center;
  &:hover {
    opacity: 0.9;
  }
`;

/* Grid */
export const TwoColGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: stretch;

  @media ${breakpoints.downMdPlus} {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: ${cardShadow};
  display: flex;
  flex-direction: column;
`;

export const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

export const CardTitle = styled.h3`
  ${headlineFont}
  font-size: 20px;
  margin: 0 0 4px;
  color: ${onSurface};
`;

export const CardSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${onVariant};
`;

export const Badge = styled.span<{ $tone: 'slate' | 'teal' }>`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
  ${({ $tone }) =>
    $tone === 'teal'
      ? css`
          background: rgba(0, 164, 166, 0.1);
          color: ${primary};
        `
      : css`
          background: #f1f5f9;
          color: #64748b;
        `}
`;

export const PlanName = styled.h4<{ $muted?: boolean }>`
  ${headlineFont}
  font-size: 24px;
  margin: 0 0 16px;
  color: ${({ $muted }) => ($muted ? '#94a3b8' : primaryLight)};
`;

export const FeatureBox = styled.div`
  padding: 16px;
  background: #f2f4f6;
  border-radius: 12px;
  margin-bottom: 28px;
`;

export const FeatureBlurb = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${onVariant};
  font-weight: 500;
  line-height: 1.5;
`;

export const FeatureList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${onVariant};

  & > svg {
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

export const PlanFooter = styled.div`
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FooterNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const SplitButtonWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

export const SplitButtonMain = styled.a`
  ${planGradient}
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  padding: 12px 20px;
  border-radius: 12px 0 0 12px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:hover {
    opacity: 0.9;
  }
`;

export const SplitButtonChevron = styled.button`
  ${planGradient}
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 12px 10px;
  border-radius: 0 12px 12px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.9;
  }
`;

export const ChoosePlanFullButton = styled.a`
  ${planGradient}
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  padding: 12px 16px 12px 20px;
  border-radius: 12px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  &:hover {
    opacity: 0.9;
  }
`;

export const DropdownPanel = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 256px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 50, 51, 0.12);
  border: 1px solid rgba(188, 201, 201, 0.2);
  z-index: 30;
  padding: 4px 0;
  overflow: hidden;
`;

export const DropdownItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${onSurface};
  text-decoration: none;
  &:hover {
    background: #f8fafc;
  }
`;

/* Payment column */
export const PaymentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

export const SwitchBillingModeError = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.color.bittersweet};
  font-weight: 500;
  line-height: 1.45;
`;

export const PaymentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

export const PaymentCopy = styled.div`
  min-width: 0;
  flex: 1;
`;

export const PaymentIconWrap = styled.div<{ $card?: boolean }>`
  width: ${({ $card }) => ($card ? 'auto' : '36px')};
  height: ${({ $card }) => ($card ? 'auto' : '36px')};
  min-width: ${({ $card }) => ($card ? '0' : '36px')};
  border-radius: ${({ $card }) => ($card ? '0' : '12px')};
  background: ${({ $card }) => ($card ? 'transparent' : '#f8fafc')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-shrink: 0;
  padding: ${({ $card }) => ($card ? '2px 0' : '0')};
`;

export const PaymentLabel = styled.p`
  margin: 0 0 2px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
`;

export const PaymentValue = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${onSurface};
`;

export const BillToLine = styled.p`
  margin: 6px 0 0;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.4;
  min-width: 0;
`;

export const BillToEmails = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

export const BillToEmail = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BillToMore = styled.span`
  flex-shrink: 0;
  font-weight: 700;
  color: ${primary};
  cursor: default;
  white-space: nowrap;
`;

export const InfoNote = styled.div`
  padding: 14px;
  background: #f2f4f6;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 500;
  color: ${onVariant};
`;

export const SmallHint = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 10px;
  color: #94a3b8;
  text-align: left;
`;

/* Usage */
export const UsageCard = styled(Card)`
  padding: 28px;
`;

export const UsageHeader = styled.div`
  display: flex;
  align-items: center;
  text-align: left;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

export const UsageHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
`;

export const UsageGrid = styled.div<{ $dimmed?: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px 40px;
  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.4;
      pointer-events: none;
      user-select: none;
    `}

  @media ${breakpoints.downMdPlus} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const UsageMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const UsageMetricTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

export const UsageLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${onSurface};
`;

export const UsageFraction = styled.span<{
  $tone?: 'default' | 'amber' | 'danger';
}>`
  font-size: 12px;
  font-weight: 800;
  color: ${({ $tone }) =>
    $tone === 'danger'
      ? '#e24b4a'
      : $tone === 'amber'
      ? '#f5a623'
      : primaryLight};
`;

export const UsageFractionMuted = styled(UsageFraction)`
  color: #94a3b8;
`;

export const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
`;

export const BarFill = styled.div<{
  $pct: number;
  $tone?: 'default' | 'amber' | 'danger';
}>`
  height: 100%;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? '#e24b4a'
      : $tone === 'amber'
      ? '#f5a623'
      : primaryLight};
  width: ${({ $pct }) => $pct}%;
  transition: width 0.6s ease-out;
`;

/* Footer trust - matches checkout order summary payment strip + Stripe note */
export const FooterPaymentTrustCard = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.color.aquaSqueeze};
`;

export const FooterStripeNote = styled.div`
  // background: ${({ theme }) => theme.color.white};
  text-align: center;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px 10px;
  flex-wrap: wrap;
  box-sizing: border-box;
`;

/** Outer wrapper for the checkout-style payment trust block on the subscription page */
export const TrustRow = styled.div`
  width: 100%;
  padding-top: 8px;
`;

const onboardingConfirmEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const confirmingDotPulse = keyframes`
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

/** Centered, card-free state while Stripe checkout syncs. */
export const CheckoutConfirmingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px 72px;
  min-height: min(440px, 58vh);
  max-width: 420px;
  margin: 0 auto;
  animation: ${onboardingConfirmEnter} 0.42s ease-out both;
`;

export const CheckoutConfirmingSpinnerRing = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 105, 107, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
`;

export const OnboardingConfirmTitle = styled.h2`
  ${headlineFont}
  font-size: 22px;
  margin: 0;
  color: ${onSurface};
  text-align: center;
  letter-spacing: -0.02em;
`;

export const OnboardingConfirmHint = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${onVariant};
  text-align: center;
  max-width: 340px;
`;

export const CheckoutConfirmingDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 28px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #94a3b8;
    animation: ${confirmingDotPulse} 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
`;

/** Fade/slide in when switching from confirming loader to checkout outcome. */
export const CheckoutOutcomeEnter = styled.div`
  animation: ${onboardingConfirmEnter} 0.38s ease-out both;
`;
